import React, { useDeferredValue, useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Globe, User } from "lucide-react";
import { PreviewData } from "@/hooks/useSelectedRowPreview";
import { useCachedPagePicture } from "@/hooks/useCachedPagePicture";

const CTA_LABELS: Record<string, string> = {
  NO_BUTTON: "",
  SHOP_NOW: "Shop Now",
  LEARN_MORE: "Learn More",
  SIGN_UP: "Sign Up",
  DOWNLOAD: "Download",
  GET_OFFER: "Get Offer",
  BOOK_NOW: "Book Now",
  CONTACT_US: "Contact Us",
  SUBSCRIBE: "Subscribe",
  GET_QUOTE: "Get Quote",
  APPLY_NOW: "Apply Now",
  ORDER_NOW: "Order Now",
  WATCH_MORE: "Watch More",
  SEE_MENU: "See Menu",
  SEND_MESSAGE: "Send Message",
  WHATSAPP_MESSAGE: "Send WhatsApp Message",
  CALL_NOW: "Call Now",
};

interface AdPreviewPanelProps {
  previewData: PreviewData | null;
  userId: string | undefined;
  className?: string;
}

function AdPreviewPanelInner({
  previewData,
  userId,
  className,
}: AdPreviewPanelProps) {
  const deferredData = useDeferredValue(previewData);
  const [activeCardIndex, setActiveCardIndex] = useState(0);
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    setImgError(false);
  }, [deferredData?.facebookPageId]);

  useEffect(() => {
    setActiveCardIndex(0);
  }, [deferredData?.adName]);

  const { data: pageData } = useCachedPagePicture(
    deferredData?.facebookPageId,
    userId
  );

  const ctaLabel =
    CTA_LABELS[deferredData?.ctaType || "LEARN_MORE"] || "Learn More";

  const displayLink =
    deferredData?.displayLink ||
    (() => {
      try {
        return new URL(deferredData?.destinationUrl || "").hostname;
      } catch {
        return "";
      }
    })();

  const isCatalog = deferredData?.adFormat?.toLowerCase().includes("catalog");
  const isCarousel = deferredData?.adFormat?.toLowerCase().includes("carousel") && (deferredData?.carouselCards?.length ?? 0) > 0;

  const thumbnailUrl =
    deferredData?.creativeSquareThumbnailUrl ||
    deferredData?.creativeVerticalThumbnailUrl ||
    deferredData?.flexibleFirstAssetThumbnail ||
    null;

  return (
    <div className={cn("p-4", className)}>
      <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">
        Ad Preview
      </div>

      {!deferredData ? (
        <div className="flex items-center justify-center py-12">
          <p className="text-sm text-muted-foreground">
            No preview data
          </p>
        </div>
      ) : isCatalog ? (
        <div className="space-y-0 rounded-lg border border-border bg-card overflow-hidden shadow-sm">
          <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
            <p className="text-sm font-medium text-muted-foreground">Preview not available for Catalog ads</p>
            <p className="text-xs text-muted-foreground/70 mt-1">Product data is managed by Meta's catalog system</p>
          </div>
        </div>
      ) : (
        <div className="space-y-0 rounded-lg border border-border bg-card overflow-hidden shadow-sm">
          {/* Page Header */}
          <div className="flex items-center gap-2 p-3">
            <div className="h-9 w-9 rounded-full bg-muted flex items-center justify-center overflow-hidden flex-shrink-0">
              {pageData?.pictureUrl && !pageData.isSilhouette && !imgError ? (
                <img
                  src={pageData.pictureUrl}
                  alt=""
                  className="h-full w-full object-cover rounded-full"
                  onError={() => setImgError(true)}
                />
              ) : (
                <User className="h-4 w-4 text-muted-foreground" />
              )}
            </div>
            <div className="flex flex-col min-w-0">
              <span className="text-sm font-semibold text-foreground truncate">
                {pageData?.pageName || deferredData.facebookPageName || "Page Name"}
              </span>
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                Sponsored
                <span>·</span>
                <Globe className="h-3 w-3" />
              </span>
            </div>
          </div>

          {/* Primary Text */}
          <div className="px-3 pb-2">
            <p className="text-sm text-foreground whitespace-pre-wrap break-words">
              {deferredData.primaryText || (
                <span className="text-muted-foreground italic">
                  Primary text will appear here...
                </span>
              )}
            </p>
          </div>

          {isCarousel ? (
            <>
              {/* Single card display with navigation */}
              <div className="relative">
                {(() => {
                  const card = deferredData.carouselCards[activeCardIndex];
                  if (!card) return null;
                  return (
                    <>
                      <div className="aspect-square bg-muted/50 relative overflow-hidden">
                        {card.thumbnailUrl ? (
                          <img src={card.thumbnailUrl} alt={`Card ${activeCardIndex + 1}`} className="w-full h-full object-contain" />
                        ) : (
                          <div className="absolute inset-0 flex items-center justify-center text-muted-foreground">
                            <span className="text-xs">Card {activeCardIndex + 1}</span>
                          </div>
                        )}
                      </div>
                      <div className="bg-muted/20 border-t border-border px-3 py-2.5">
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            {displayLink && (
                              <p className="text-xs text-muted-foreground uppercase truncate">{displayLink}</p>
                            )}
                            <p className="text-sm font-semibold text-foreground truncate">
                              {card.headline || `Card ${activeCardIndex + 1}`}
                            </p>
                            {card.description && (
                              <p className="text-xs text-muted-foreground truncate">{card.description}</p>
                            )}
                          </div>
                          {ctaLabel && (
                            <Button variant="secondary" size="sm" className="flex-shrink-0 text-xs h-8 pointer-events-none">
                              {ctaLabel}
                            </Button>
                          )}
                        </div>
                      </div>
                    </>
                  );
                })()}

                {/* Left arrow */}
                {activeCardIndex > 0 && (
                  <button
                    onClick={() => setActiveCardIndex(activeCardIndex - 1)}
                    className="absolute left-2 top-1/3 -translate-y-1/2 bg-background/90 hover:bg-background rounded-full shadow-md h-8 w-8 flex items-center justify-center transition-colors"
                  >
                    <ChevronLeft className="h-4 w-4 text-foreground" />
                  </button>
                )}

                {/* Right arrow */}
                {activeCardIndex < deferredData.carouselCards.length - 1 && (
                  <button
                    onClick={() => setActiveCardIndex(activeCardIndex + 1)}
                    className="absolute right-2 top-1/3 -translate-y-1/2 bg-background/90 hover:bg-background rounded-full shadow-md h-8 w-8 flex items-center justify-center transition-colors"
                  >
                    <ChevronRight className="h-4 w-4 text-foreground" />
                  </button>
                )}
              </div>

              {/* Dot indicators */}
              <div className="flex justify-center gap-1.5 py-2">
                {deferredData.carouselCards.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveCardIndex(index)}
                    className={cn(
                      "h-1.5 rounded-full transition-all",
                      index === activeCardIndex ? "w-4 bg-foreground/60" : "w-1.5 bg-muted-foreground/30"
                    )}
                  />
                ))}
              </div>
            </>
          ) : (
            <>
              {/* Creative Asset — Single / Flexible / Collection */}
              <div className="w-full aspect-square bg-muted/50 flex items-center justify-center">
                {thumbnailUrl ? (
                  <img
                    src={thumbnailUrl}
                    alt="Ad creative"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="flex flex-col items-center gap-2 text-muted-foreground">
                    <div className="h-12 w-12 rounded-lg border-2 border-dashed border-muted-foreground/30 flex items-center justify-center">
                      <span className="text-xl font-light">+</span>
                    </div>
                    <span className="text-xs">Select a creative</span>
                  </div>
                )}
              </div>

              {/* Link / Headline / Description / CTA */}
              <div className="p-3 bg-muted/20">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex flex-col min-w-0 flex-1">
                    {displayLink && (
                      <span className="text-xs text-muted-foreground uppercase truncate">
                        {displayLink}
                      </span>
                    )}
                    <span className="text-sm font-semibold text-foreground line-clamp-2">
                      {deferredData.headline || (
                        <span className="text-muted-foreground italic font-normal">
                          Headline...
                        </span>
                      )}
                    </span>
                    {deferredData.description && (
                      <span className="text-xs text-muted-foreground line-clamp-1 mt-0.5">
                        {deferredData.description}
                      </span>
                    )}
                  </div>
                  {ctaLabel && (
                    <Button variant="secondary" size="sm" className="flex-shrink-0 text-xs h-8 pointer-events-none">
                      {ctaLabel}
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {deferredData && (
        <p className="text-[10px] text-muted-foreground text-center mt-3">
          Facebook Feed · {deferredData.adFormat || "Single"} format
        </p>
      )}
    </div>
  );
}

export const AdPreviewPanel = React.memo(AdPreviewPanelInner);
